import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import {
  AccrualTab,
  AccrualsV2ApiApiV2AccrualsPkEmployeeIdAccrualPayTypesGetRequest,
  EmployeeCacheKeys,
  AccrualsV2ApiApiV2AccrualsPkEmployeeIdAuditRecordIdGetRequest,
  AccrualsV2ApiApiV2AccrualsPkEmployeeIdBalanceGetRequest,
  AccrualsV2ApiApiV2AccrualsPkEmployeeIdFormSetupGetRequest,
  AccrualsV2ApiApiV2AccrualsPkEmployeeIdRecomputePutRequest,
  AccrualsV2ApiApiV2AccrualsPkEmployeeIdTabSetupGetRequest,
  useApi,
} from '..';

const AccrualTabMap = {
  0: 'balance',
  1: 'details',
  2: 'annualEntitlements',
  3: 'adjustments',
} as const;
type AccrualsTabType = (typeof AccrualTabMap)[keyof typeof AccrualTabMap];

interface UseAccrualsTabParamsDataGet {
  employeeId: number;
  tabIndex: AccrualTab;
  payTypeId?: number;
}

interface UseAccrualsTabParams {
  employeeId: number;
  tabIndex: AccrualTab;
}

enum AccrualsKeys {
  ACCRUALS_BALANCE = 'time-card-accruals-balance',
  ACCRUALS_TAB_SETUP = 'time-card-accruals-tab-setup',
  ACCRUALS_ALL_TABS = 'time-card-accruals-all-tabs',

  ACCRUALS_BALANCE_TAB_DATA = 'time-card-accruals-balance-tab-data',
  ACCRUALS_DETAILS_TAB_DATA = 'time-card-accruals-details-tab-data',
  ACCRUALS_ANNUAL_ENTITLEMENTS_TAB_DATA = 'time-card-accruals-annual-entitlements-tab-data',
  ACCRUALS_ADJUSTMENTS_TAB_DATA = 'time-card-accruals-adjustments-tab-data',

  ACCRUAL_PAY_TYPES = 'time-card-accrual-pay-types',
  ACCRUAL_AUDIT = 'time-card-accrual-audit',
  ACCRUAL_AUDIT_FORM_SETUP = 'time-card-accrual-form-setup',
}

const QueryDataKeyMap: Record<AccrualsTabType, string> = {
  balance: AccrualsKeys.ACCRUALS_BALANCE_TAB_DATA,
  details: AccrualsKeys.ACCRUALS_DETAILS_TAB_DATA,
  annualEntitlements: AccrualsKeys.ACCRUALS_ANNUAL_ENTITLEMENTS_TAB_DATA,
  adjustments: AccrualsKeys.ACCRUALS_ADJUSTMENTS_TAB_DATA,
};
export const useUpdateAccrualsCache = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      queryClient
        .invalidateQueries({ queryKey: ['accruals_data'], exact: false })
        .catch(noop),
      queryClient
        .invalidateQueries({
          queryKey: [AccrualsKeys.ACCRUALS_BALANCE],
          exact: false,
        })
        .catch(noop),
      queryClient
        .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
        .catch(noop),
    ]);
  }, [queryClient]);
};
export const useAccrualsBalanceGet = (
  body: AccrualsV2ApiApiV2AccrualsPkEmployeeIdBalanceGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [AccrualsKeys.ACCRUALS_BALANCE, body],
    queryFn: () =>
      api.accrualsV2Api
        .apiV2AccrualsPkEmployeeIdBalanceGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: body.pkEmployeeId > 0,
  });
};
export const useAccrualsTabSetupGet = (
  body: AccrualsV2ApiApiV2AccrualsPkEmployeeIdTabSetupGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [AccrualsKeys.ACCRUALS_TAB_SETUP, body],
    queryFn: () =>
      api.accrualsV2Api
        .apiV2AccrualsPkEmployeeIdTabSetupGet(body)
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: body.pkEmployeeId > 0,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 60,
  });
};
export const useAccrualsAllTabsGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [AccrualsKeys.ACCRUALS_ALL_TABS],
    queryFn: () =>
      api.accrualsV2Api
        .apiV2AccrualsAllTabsGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 60,
  });
};
export const useAccrualsTabDataGet = ({
  employeeId,
  tabIndex,
  payTypeId,
}: UseAccrualsTabParamsDataGet) => {
  const api = useApi();

  const tab = AccrualTabMap[tabIndex];
  if (!tab) throw new Error(`Invalid tab index: ${tabIndex}`);

  const endpointsMap: Record<AccrualsTabType, (params: any) => Promise<any>> = {
    balance: (params) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdBalancesTabGet(params),
    details: (params) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdDetailsTabGet(params),
    annualEntitlements: (params) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAnnualEntitlementOverridesGet(
        params
      ),
    adjustments: (params) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAdjustmentsGet(params),
  };

  const requestParams =
    tab === 'details' && payTypeId
      ? { pkEmployeeId: employeeId, payTypeId }
      : { pkEmployeeId: employeeId };

  return useQuery({
    queryKey: [
      'accruals_data',
      QueryDataKeyMap[tab],
      employeeId,
      tab === 'details' ? payTypeId : undefined,
    ],
    queryFn: async () => (await endpointsMap[tab](requestParams)).data ?? [],
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: employeeId > 0,
    staleTime: 1000 * 60 * 60,
  });
};
export const useAccrualsEmployeeIdAccrualPayTypesGet = (
  body: AccrualsV2ApiApiV2AccrualsPkEmployeeIdAccrualPayTypesGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [AccrualsKeys.ACCRUAL_PAY_TYPES, body],
    queryFn: () =>
      api.accrualsV2Api
        .apiV2AccrualsPkEmployeeIdAccrualPayTypesGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: body.pkEmployeeId > 0,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 60,
  });
};
export const useAccrualsRecordDelete = ({
  employeeId,
  tabIndex,
}: UseAccrualsTabParams) => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  const tab = AccrualTabMap[tabIndex];
  if (!tab) throw new Error(`Invalid tab index: ${tabIndex}`);

  const endpointsMap: Partial<
    Record<AccrualsTabType, (params: any) => Promise<any>>
  > = {
    annualEntitlements: (params) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAnnualEntitlementsDelete(
        params
      ),
    adjustments: (params) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAdjustmentsDelete(params),
  };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (recordId: number) => {
      const endpoint = endpointsMap[tab];
      if (!endpoint) {
        return null;
      }
      const requestParams = {
        pkEmployeeId: employeeId,
        deleteAccrualRequest: { recordId },
      };
      return (await endpoint(requestParams)).data;
    },
    onSuccess: async () => {
      await queryClient
        .invalidateQueries({
          queryKey: ['accruals_data', QueryDataKeyMap[tab]],
          exact: false,
        })
        .catch(noop);
      queryClient
        .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
        .catch(noop);
    },
    onError: (error: any) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
export const useAccrualsAuditGet = (
  body: AccrualsV2ApiApiV2AccrualsPkEmployeeIdAuditRecordIdGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [AccrualsKeys.ACCRUAL_AUDIT, body],
    queryFn: () =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAuditRecordIdGet(body),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: body.pkEmployeeId > 0,
  });
};
export const useAccrualsFormSetupGet = (
  body: AccrualsV2ApiApiV2AccrualsPkEmployeeIdFormSetupGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [AccrualsKeys.ACCRUAL_AUDIT_FORM_SETUP, body],
    queryFn: () =>
      api.accrualsV2Api
        .apiV2AccrualsPkEmployeeIdFormSetupGet(body)
        .then((response) => response?.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: body.pkEmployeeId > 0 && (body.tab === 2 || body.tab === 3),
    refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
  });
};
export const useAccrualsRecordAdd = ({
  employeeId,
  tabIndex,
}: UseAccrualsTabParams) => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  const tab = AccrualTabMap[tabIndex];
  if (!tab) throw new Error(`Invalid tab index: ${tabIndex}`);

  const endpointsMap: Partial<
    Record<
      AccrualsTabType,
      (employeeId: number, newRecord: any) => Promise<any>
    >
  > = {
    annualEntitlements: (employeeId, newRecord) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAnnualEntitlementsPost({
        pkEmployeeId: employeeId,
        addAccrualAnnualEntitlementRequest: newRecord,
      }),
    adjustments: (employeeId, newRecord) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAdjustmentsPost({
        pkEmployeeId: employeeId,
        addAccrualAdjustmentRequest: newRecord,
      }),
  };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newRecord: any) => {
      const endpoint = endpointsMap[tab];
      if (!endpoint) {
        return null;
      }
      return (await endpoint(employeeId, newRecord)).data;
    },
    onSuccess: async () => {
      await queryClient
        .invalidateQueries({
          queryKey: ['accruals_data', QueryDataKeyMap[tab]],
          exact: false,
        })
        .catch(noop);
      queryClient
        .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
        .catch(noop);
    },
    onError: (error: any) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
export const useAccrualsRecordUpdate = ({
  employeeId,
  tabIndex,
}: UseAccrualsTabParams) => {
  const api = useApi();
  const { notifyError } = useSnackBar();

  const tab = AccrualTabMap[tabIndex];
  if (!tab) throw new Error(`Invalid tab index: ${tabIndex}`);

  const endpointsMap: Partial<
    Record<
      AccrualsTabType,
      (employeeId: number, newRecord: any) => Promise<any>
    >
  > = {
    annualEntitlements: (employeeId, newRecord) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAnnualEntitlementsPut({
        pkEmployeeId: employeeId,
        updateAccrualAnnualEntitlementRequest: newRecord,
      }),
    adjustments: (employeeId, newRecord) =>
      api.accrualsV2Api.apiV2AccrualsPkEmployeeIdAdjustmentsPut({
        pkEmployeeId: employeeId,
        updateAccrualAdjustmentRequest: newRecord,
      }),
  };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newRecord: any) => {
      const endpoint = endpointsMap[tab];
      if (!endpoint) {
        return null;
      }
      return (await endpoint(employeeId, newRecord)).data;
    },
    onSuccess: async () => {
      await queryClient
        .invalidateQueries({ queryKey: [QueryDataKeyMap[tab]], exact: false })
        .catch(noop);
      queryClient
        .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
        .catch(noop);
    },
    onError: (error: any) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};

export const useAccrualsRecalculate = () => {
  const api = useApi();
  const { notifyError } = useSnackBar();
  const UpdateAccrualsCache = useUpdateAccrualsCache();
  return useMutation({
    mutationFn: async (
      body: AccrualsV2ApiApiV2AccrualsPkEmployeeIdRecomputePutRequest
    ) => {
      return (
        await api.accrualsV2Api.apiV2AccrualsPkEmployeeIdRecomputePut(body)
      ).data;
    },
    onSuccess: async () => {
      await UpdateAccrualsCache();
    },
    onError: (error: any) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
